import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, Mail } from "lucide-react";
import logo from "./LOGO.png";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const navItems = [
    { name: "Home", path: "/" },
    { name: "About Us", path: "/about" },
    { name: "Vision & Mission", path: "/vision-and-mission" },
    { name: "Our Brands", path: "/brand" },
    { name: "Contact", path: "/contact" },
  ];

  return (
    <>
      <header className={`fixed w-full z-50 transition-all duration-500 border-b border-gray-200 ${
        scrolled 
          ? "bg-white/95 backdrop-blur-md shadow-lg" 
          : "bg-gradient-to-r from-blue-50 via-white to-orange-50"
      }`}>
        {/* Top Contact Bar */}
        <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white py-2">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between text-xs">
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-1">
                  <Phone className="h-3 w-3" />
                  <span>+91 9961362234</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Mail className="h-3 w-3" />
                  <span>info@panagiagroup.com</span>
                </div>
              </div>
              <div className="hidden sm:block">
                <span className="text-orange-300">Excellence in Every Detail</span>
              </div>
            </div>
          </div>
        </div>

        {/* Main Navbar */}
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex-shrink-0 flex items-center space-x-3 group">
              <div className="relative">
                <img src={logo} alt="PANAGIA GROUP Logo" className="h-14 w-auto transition-transform duration-300 group-hover:scale-105" />
                {/* <div className="absolute inset-0 bg-gradient-to-r from-orange-400 to-orange-600 opacity-0 group-hover:opacity-20 rounded-full transition-opacity duration-300"></div> */}
              </div>
              <div className="block">
                <h1 className="text-lg sm:text-xl font-bold bg-gradient-to-r from-blue-900 to-blue-700 bg-clip-text text-transparent">
                  PANAGIA GROUP
                </h1>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <div className="flex space-x-1">
                {navItems.map((item) => (
                  <Link
                    key={item.name}
                    to={item.path}
                    className={`relative px-4 py-2 text-sm font-medium rounded-lg transition-all duration-300 group ${
                      location.pathname === item.path 
                        ? "text-orange-600 bg-orange-50" 
                        : "text-blue-900 hover:text-orange-600 hover:bg-orange-50"
                    }`}
                  >
                    <span className="relative z-10">{item.name}</span>
                    <div className={`absolute bottom-0 left-1/2 transform -translate-x-1/2 h-0.5 bg-gradient-to-r from-orange-500 to-orange-600 transition-all duration-300 ${
                      location.pathname === item.path ? "w-full" : "w-0 group-hover:w-full"
                    }`}></div>
                  </Link>
                ))}
              </div>
              
              {/* CTA Button */}
              <Link
                to="/contact"
                className="relative inline-flex items-center px-6 py-3 text-sm font-semibold rounded-full text-white bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                <span className="relative z-10">Get Quote</span>
                <div className="absolute inset-0 bg-gradient-to-r from-orange-600 to-orange-700 rounded-full opacity-0 hover:opacity-100 transition-opacity duration-300"></div>
              </Link>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden flex items-center">
              <button
                onClick={() => setIsOpen(true)}
                className="inline-flex items-center justify-center p-3 rounded-full text-blue-900 hover:text-orange-600 hover:bg-orange-50 focus:outline-none transition-all duration-300 transform hover:scale-110"
              >
                <Menu className="h-6 w-6" />
              </button>
            </div>
          </div>
        </nav>
      </header>

      {/* Mobile Menu - Outside header to avoid positioning issues */}
      {isOpen && (
        <div className="fixed inset-0 z-[9999]">
          {/* Overlay */}
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Mobile Menu Panel */}
          <div className="absolute inset-y-0 left-0 w-80 bg-white shadow-2xl">
            {/* Mobile menu content */}
            <div className="h-full overflow-y-auto py-8 px-6">
              <div className="flex items-center justify-start mb-8">
                <div className="flex items-center space-x-3">
                  <img src={logo} alt="PANAGIA GROUP Logo" className="h-12 w-auto" />
                  <div>
                    <h2 className="text-lg font-bold text-blue-900">
                      PANAGIA GROUP
                    </h2>
                  </div>
                </div>
              </div>
              
              <nav className="flex flex-col space-y-2">
                {navItems.map((item) => (
                  <Link
                    key={item.name}
                    to={item.path}
                    onClick={() => setIsOpen(false)}
                    className={`flex items-center px-4 py-3 rounded-lg text-base font-medium transition-all duration-200 ${
                      location.pathname === item.path 
                        ? "text-orange-600 bg-orange-50" 
                        : "text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    {item.name}
                  </Link>
                ))}
                
                <div className="pt-4">
                  <Link
                    to="/contact"
                    className="flex items-center justify-center w-full px-4 py-3 text-base font-semibold text-white bg-orange-500 rounded-lg hover:bg-orange-600 transition-colors duration-200"
                  >
                    Get Quote
                  </Link>
                </div>
              </nav>

              {/* Contact Info in Mobile Menu */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <div className="space-y-3">
                  <div className="flex items-center space-x-3 text-sm text-gray-600">
                    <Phone className="h-4 w-4 text-orange-500" />
                    <span>+91 9961362234</span>
                  </div>
                  <div className="flex items-center space-x-3 text-sm text-gray-600">
                    <Mail className="h-4 w-4 text-orange-500" />
                    <span>info@panagiagroup.com</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Close button - positioned outside menu panel */}
          <div className="absolute top-4 left-80 ml-4">
            <button
              onClick={() => setIsOpen(false)}
              className="flex items-center justify-center h-12 w-12 rounded-full bg-gradient-to-r from-blue-900 to-blue-800 text-white focus:outline-none focus:ring-2 focus:ring-white transform hover:scale-110 transition-all duration-300 shadow-lg"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;